import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Snackbar,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tab,
  Tabs,
} from '@material-ui/core';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Info as InfoIcon,
  Assignment as OperationsIcon,
  Description as InstructionsIcon,
  Build as ToolsIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@material-ui/icons';
import { RootState } from '../../store';
import {
  fetchRouteCardByIdStart,
  fetchDropdownsStart,
  createRouteCardStart,
  updateRouteCardStart,
  fetchOperationsStart,
  addOperationStart,
  updateOperationStart,
  deleteOperationStart,
  clearSelectedRouteCard,
  clearError,
  setOperationsPanel,
} from '../../store/reducers/routeCardSlice';
import {
  routeCardApi,
  RouteCard,
  CreateRouteCardRequest,
  RouteCardOperation,
  Operation,
} from '../../services/routeCardApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`routecard-tabpanel-${index}`}
      aria-labelledby={`routecard-tab-${index}`}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

/**
 * RouteCardEdit component for creating and editing route cards with operations management
 */
const RouteCardEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEdit = !!id;

  const {
    selectedRouteCard,
    loading,
    error,
    clients,
    parts,
    operations,
    selectedOperations,
    operationsLoading,
    loadingDropdowns,
  } = useSelector((state: RootState) => state.routeCard);

  // Local state
  const [currentTab, setCurrentTab] = useState(0);
  const [formData, setFormData] = useState<Partial<RouteCard>>({
    Name: '',
    Description: '',
    Issue: '1',
    Version: '1',
    PartId: '',
    ClientId: '',
    EstimatedTime: 0,
    Active: true,
  });
  const [operationDialogOpen, setOperationDialogOpen] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState<RouteCardOperation | null>(null);
  const [operationFormData, setOperationFormData] = useState({
    OperationId: '',
    Sequence: 1,
    EstimatedTime: 0,
    SetupTime: 0,
    TeardownTime: 0,
    Active: true,
  });
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [routeCardData, setRouteCardData] = useState<RouteCard | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchDropdownsStart());
    
    if (isEdit && id) {
      loadRouteCardData(id);
    }

    return () => {
      dispatch(clearSelectedRouteCard());
    };
  }, [dispatch, isEdit, id]);

  const loadRouteCardData = async (routeCardId: string) => {
    setLoadingData(true);
    setApiError(null);
    try {
      const rcData = await routeCardApi.getById(routeCardId);
      setRouteCardData(rcData);
      
      // Also dispatch Redux actions for backward compatibility
      dispatch(fetchRouteCardByIdStart(routeCardId));
    } catch (error: any) {
      console.error('Error loading route card:', error);
      setApiError(error.message || 'Failed to load route card data. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  // Update form data when route card is loaded
  useEffect(() => {
    const currentRouteCard = routeCardData || selectedRouteCard;
    if (currentRouteCard && isEdit) {
      setFormData({
        ...currentRouteCard,
      });
    }
  }, [routeCardData, selectedRouteCard, isEdit]);

  const handleInputChange = (field: keyof RouteCard) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const validateRouteCard = (rcData: Partial<RouteCard>): string[] => {
    const errors: string[] = [];
    
    // Required fields validation (matching backend schema)
    if (!rcData.Name?.trim()) {
      errors.push('Route Card Name is required');
    } else if (rcData.Name.length > 50) {
      errors.push('Route Card Name must be 50 characters or less');
    }
    
    if (!rcData.Description?.trim()) {
      errors.push('Description is required');
    } else if (rcData.Description.length > 150) {
      errors.push('Description must be 150 characters or less');
    }
    
    if (!rcData.PartId?.trim()) {
      errors.push('Part is required');
    } else if (isNaN(parseInt(rcData.PartId))) {
      errors.push('Part ID must be a valid number');
    }
    
    if (!rcData.ClientId?.trim()) {
      errors.push('Client is required');
    } else if (isNaN(parseInt(rcData.ClientId))) {
      errors.push('Client ID must be a valid number');
    }
    
    return errors;
  };

  const handleClientChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const clientId = event.target.value as string;
    setFormData(prev => ({
      ...prev,
      ClientId: clientId,
      PartId: '', // Reset part when client changes
    }));
  };

  const handleSave = async () => {
    setValidationErrors([]);
    
    // Validate the route card data
    const errors = validateRouteCard(formData);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        // Update existing route card
        const updatedRC = await routeCardApi.update({ ...formData, id } as RouteCard);
        setRouteCardData(updatedRC);
        dispatch(updateRouteCardStart({ ...formData, id } as RouteCard));
      } else {
        // Create new route card
        const createData: CreateRouteCardRequest = {
          Name: formData.Name!,
          Description: formData.Description!,
          Issue: formData.Issue!,
          Version: formData.Version!,
          PartId: formData.PartId!,
          ClientId: formData.ClientId!,
        };
        const newRC = await routeCardApi.create(createData);
        setRouteCardData(newRC);
        dispatch(createRouteCardStart(createData));
      }
      
      navigate('/routecards');
    } catch (error: any) {
      console.error('Error saving route card:', error);
      setValidationErrors([error.message || 'Failed to save route card. Please try again.']);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/routecards');
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setCurrentTab(newValue);
    
    // Load operations when switching to operations tab
    if (newValue === 1 && isEdit && id) {
      dispatch(fetchOperationsStart(id));
    }
  };

  // Sequence Management Functions (matching Angular logic exactly)
  const handleMoveUp = (operation: RouteCardOperation) => {
    if (operation.Sequence === 1) {
      setSnackbarMessage('Cannot move the first operation up');
      return;
    }

    // Find the previous operation (sequence - 1)
    const previousOperation = selectedOperations.find(op => op.Sequence === operation.Sequence - 1);
    
    if (!previousOperation) {
      setSnackbarMessage('Cannot find previous operation');
      return;
    }

    // Swap sequences: previous goes down, current goes up
    const updatedOperations = selectedOperations.map(op => {
      if (op.id === operation.id) {
        return { ...op, Sequence: op.Sequence - 1 };
      } else if (op.id === previousOperation.id) {
        return { ...op, Sequence: op.Sequence + 1 };
      }
      return op;
    });

    // Update both operations in the backend
    dispatch(updateOperationStart({ ...operation, Sequence: operation.Sequence - 1 }));
    dispatch(updateOperationStart({ ...previousOperation, Sequence: previousOperation.Sequence + 1 }));
  };

  const handleMoveDown = (operation: RouteCardOperation) => {
    if (operation.Sequence >= selectedOperations.length) {
      setSnackbarMessage('Cannot move the last operation down');
      return;
    }

    // Find the next operation (sequence + 1)
    const nextOperation = selectedOperations.find(op => op.Sequence === operation.Sequence + 1);
    
    if (!nextOperation) {
      setSnackbarMessage('Cannot find next operation');
      return;
    }

    // Swap sequences: next goes up, current goes down
    const updatedOperations = selectedOperations.map(op => {
      if (op.id === operation.id) {
        return { ...op, Sequence: op.Sequence + 1 };
      } else if (op.id === nextOperation.id) {
        return { ...op, Sequence: op.Sequence - 1 };
      }
      return op;
    });

    // Update both operations in the backend
    dispatch(updateOperationStart({ ...operation, Sequence: operation.Sequence + 1 }));
    dispatch(updateOperationStart({ ...nextOperation, Sequence: nextOperation.Sequence - 1 }));
  };

  // Operations Management
  const handleAddOperation = () => {
    setSelectedOperation(null);
    setOperationFormData({
      OperationId: '',
      Sequence: selectedOperations.length + 1,
      EstimatedTime: 0,
      SetupTime: 0,
      TeardownTime: 0,
      Active: true,
    });
    setOperationDialogOpen(true);
  };

  const handleEditOperation = (operation: RouteCardOperation) => {
    setSelectedOperation(operation);
    setOperationFormData({
      OperationId: operation.OperationId,
      Sequence: operation.Sequence,
      EstimatedTime: operation.EstimatedTime,
      SetupTime: operation.SetupTime,
      TeardownTime: operation.TeardownTime,
      Active: operation.Active,
    });
    setOperationDialogOpen(true);
  };

  const handleDeleteOperation = (operation: RouteCardOperation) => {
    if (operation.id) {
      // Delete the operation
      dispatch(deleteOperationStart(operation.id));
      
      // Renumber subsequent operations (matching Angular logic)
      const deleteSequence = operation.Sequence;
      selectedOperations.forEach(op => {
        if (op.Sequence > deleteSequence && op.id !== operation.id) {
          dispatch(updateOperationStart({ ...op, Sequence: op.Sequence - 1 }));
        }
      });
    }
  };

  const handleOperationSave = () => {
    if (!id) return;

    if (selectedOperation) {
      // Update existing operation
      dispatch(updateOperationStart({
        ...selectedOperation,
        ...operationFormData,
      }));
    } else {
      // Add new operation
      dispatch(addOperationStart({
        routeCardId: id,
        operation: operationFormData,
      }));
    }
    
    setOperationDialogOpen(false);
    setSelectedOperation(null);
  };

  const handleOperationCancel = () => {
    setOperationDialogOpen(false);
    setSelectedOperation(null);
  };

  const handleErrorClose = () => {
    dispatch(clearError());
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  const getFilteredParts = () => {
    return parts.filter(part => !formData.ClientId || part.ClientId === formData.ClientId);
  };

  // Sort operations by sequence for display
  const sortedOperations = [...selectedOperations].sort((a, b) => a.Sequence - b.Sequence);

  if (loadingDropdowns) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" style={{ marginLeft: '1rem' }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Edit Route Card' : 'Add New Route Card Template'}
        </Typography>
        <Box display="flex" style={{ gap: '8px' }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || loadingData || !formData.Name || !formData.ClientId || !formData.PartId}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      {/* Error Display */}
      {(error || apiError || validationErrors.length > 0) && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={() => {
            setValidationErrors([]);
            setApiError(null);
            dispatch(clearError());
          }}
        >
          <div>
            {error && <div>{error}</div>}
            {apiError && <div>{apiError}</div>}
            {validationErrors.length > 0 && (
              <div>
                {validationErrors.map((err, index) => (
                  <div key={index}>{err}</div>
                ))}
              </div>
            )}
          </div>
        </Snackbar>
      )}

      {/* Loading Display */}
      {loadingData && (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      )}

      {/* Sequence Management Snackbar */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />

      {/* Tabs */}
      <Paper>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Route Card Details" icon={<InfoIcon />} />
          {isEdit && <Tab label="Operations" icon={<OperationsIcon />} />}
        </Tabs>

        {/* Route Card Details Tab */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            {/* Left Panel - Logo and Image */}
            <Grid item xs={12} md={3}>
              <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: '16px' }}>
                <img 
                  src="/images/operoute-logo-wo.png" 
                  alt="Operoute Logo" 
                  style={{ width: '170px' }}
                />
                <img 
                  src="/images/bg01.jpg" 
                  alt="Part" 
                  style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '8px' }}
                />
              </Box>
            </Grid>

            {/* Right Panel - Form Fields */}
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Route Card Name"
                    variant="outlined"
                    value={formData.Name || ''}
                    onChange={handleInputChange('Name')}
                    inputProps={{ maxLength: 50 }}
                    helperText={`${formData.Name?.length || 0}/50 characters`}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl required fullWidth variant="outlined">
                    <InputLabel>Client</InputLabel>
                    <Select
                      value={formData.ClientId || ''}
                      onChange={handleClientChange}
                      label="Client"
                    >
                      {clients.map((client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.Name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl required fullWidth variant="outlined">
                    <InputLabel>Part Number</InputLabel>
                    <Select
                      value={formData.PartId || ''}
                      onChange={handleInputChange('PartId')}
                      label="Part Number"
                      disabled={!formData.ClientId}
                    >
                      {getFilteredParts().map((part) => (
                        <MenuItem key={part.id} value={part.id}>
                          {part.Number} - {part.Description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Version"
                    variant="outlined"
                    type="number"
                    value={formData.Version || ''}
                    onChange={handleInputChange('Version')}
                    disabled
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Description"
                    variant="outlined"
                    multiline
                    rows={3}
                    value={formData.Description || ''}
                    onChange={handleInputChange('Description')}
                    inputProps={{ maxLength: 150 }}
                    helperText={`${formData.Description?.length || 0}/150 characters`}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Operations Tab */}
        {isEdit && (
          <TabPanel value={currentTab} index={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Route Card Operations</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddOperation}
              >
                Add Operation
              </Button>
            </Box>

            {operationsLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Move</TableCell>
                      <TableCell>Sequence</TableCell>
                      <TableCell>Operation Number</TableCell>
                      <TableCell>Operation & Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Times (min)</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedOperations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No operations defined. Click "Add Operation" to get started.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedOperations.map((operation) => (
                        <TableRow key={operation.id}>
                          {/* Move Up/Down Buttons */}
                          <TableCell>
                            <Box display="flex" flexDirection="column" alignItems="center">
                              <Tooltip title="Move Up">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveUp(operation)}
                                  disabled={operation.Sequence === 1}
                                  color="primary"
                                >
                                  <ArrowUpIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Move Down">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveDown(operation)}
                                  disabled={operation.Sequence === selectedOperations.length}
                                  color="primary"
                                >
                                  <ArrowDownIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>

                          {/* Sequence Number */}
                          <TableCell>
                            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                              {operation.Sequence}
                            </Typography>
                          </TableCell>

                          {/* Operation Number */}
                          <TableCell>
                            <Typography variant="body2">
                              {operation.Operation?.id || 'N/A'}
                            </Typography>
                          </TableCell>

                          {/* Operation Name & Type */}
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              {operation.Operation?.Name || 'Unknown Operation'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {operation.Operation?.OperationType?.Name || 'No Type'}
                            </Typography>
                          </TableCell>

                          {/* Description */}
                          <TableCell>
                            <Typography variant="body2">
                              {operation.Operation?.Description || 'No description'}
                            </Typography>
                          </TableCell>

                          {/* Time Information */}
                          <TableCell>
                            <Typography variant="caption" display="block">
                              Est: {operation.EstimatedTime}m
                            </Typography>
                            <Typography variant="caption" display="block">
                              Setup: {operation.SetupTime}m
                            </Typography>
                            <Typography variant="caption" display="block">
                              Teardown: {operation.TeardownTime}m
                            </Typography>
                          </TableCell>

                          {/* Actions */}
                          <TableCell>
                            <Box display="flex" style={{ gap: '4px' }}>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditOperation(operation)}
                                  color="primary"
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteOperation(operation)}
                                  color="secondary"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        )}
      </Paper>

      {/* Operation Dialog */}
      <Dialog open={operationDialogOpen} onClose={handleOperationCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedOperation ? 'Edit Operation' : 'Add Operation'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: '8px' }}>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Operation</InputLabel>
                <Select
                  value={operationFormData.OperationId}
                  onChange={(e) => setOperationFormData(prev => ({
                    ...prev,
                    OperationId: e.target.value as string
                  }))}
                  label="Operation"
                >
                  {operations.map((operation) => (
                    <MenuItem key={operation.id} value={operation.id}>
                      {operation.Name} - {operation.Description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Sequence"
                variant="outlined"
                type="number"
                value={operationFormData.Sequence}
                onChange={(e) => setOperationFormData(prev => ({
                  ...prev,
                  Sequence: parseInt(e.target.value) || 1
                }))}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Estimated Time (min)"
                variant="outlined"
                type="number"
                value={operationFormData.EstimatedTime}
                onChange={(e) => setOperationFormData(prev => ({
                  ...prev,
                  EstimatedTime: parseInt(e.target.value) || 0
                }))}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Setup Time (min)"
                variant="outlined"
                type="number"
                value={operationFormData.SetupTime}
                onChange={(e) => setOperationFormData(prev => ({
                  ...prev,
                  SetupTime: parseInt(e.target.value) || 0
                }))}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Teardown Time (min)"
                variant="outlined"
                type="number"
                value={operationFormData.TeardownTime}
                onChange={(e) => setOperationFormData(prev => ({
                  ...prev,
                  TeardownTime: parseInt(e.target.value) || 0
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOperationCancel}>Cancel</Button>
          <Button
            onClick={handleOperationSave}
            color="primary"
            variant="contained"
            disabled={!operationFormData.OperationId}
          >
            {selectedOperation ? 'Update' : 'Add'} Operation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RouteCardEdit; 